/**
 * Feuille de brouillon — résultat d'un calcul purement numérique.
 *
 * Quand la calculatrice est autorisée sur l'exercice, une ligne qui se termine
 * par « = » après un calcul sans lettre (« 15 × 34 = ») propose aussitôt son
 * résultat (« 510 ») : l'élève raisonne, l'appareil calcule. Pur : aucun DOM.
 */

import { ev, formatNumber, isNum, isVar, isPow, parseExpr } from '@jeraisonne/reasoner';
import { engineChips, type LineItem } from './line-items';

export interface NumericSuggestion {
  atoms: string[];
  text: string;
  value: number;
}

const OPERATORS = new Set([
  '+',
  '−',
  '×',
  '÷',
  '²',
  '√',
  '%',
  '^',
  '!',
  'parmi',
  'ln',
  'exp',
  'cos',
  'sin',
  'tan',
]);

/** Le résultat à proposer après le « = » final, sinon null. */
export function numericResultAfterEquals(line: LineItem[]): NumericSuggestion | null {
  const atoms = engineChips(line).flatMap((c) => c.atoms);
  if (atoms.length < 2 || atoms[atoms.length - 1] !== '=') return null;
  // dernier membre : entre le « = » (ou « ou ») précédent et le « = » final
  let start = atoms.length - 2;
  while (start >= 0 && atoms[start] !== '=' && atoms[start] !== '≡' && atoms[start] !== 'ou')
    start--;
  const member = atoms.slice(start + 1, -1);
  if (!member.length) return null;
  // un calcul : au moins une opération, et aucune lettre (grandeur, inconnue)
  if (!member.some((a) => OPERATORS.has(a))) return null;
  if (member.some((a) => isVar(a))) return null;
  if (!member.some((a) => isNum(a) || isPow(a))) return null;
  let value: number;
  try {
    value = ev(parseExpr(member), {});
  } catch {
    return null;
  }
  if (!Number.isFinite(value)) return null;
  // résultat EXACT seulement (√2 arrondi ferait une égalité fausse)
  const shown = formatNumber(value);
  const back = Number(shown.replace('−', '-').replace(',', '.'));
  if (Math.abs(back - value) > 1e-9 * Math.max(1, Math.abs(value))) return null;
  const abs = shown.replace('−', '');
  return {
    atoms: value < 0 ? ['−', abs] : [abs],
    text: shown,
    value,
  };
}
